import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Button, Input, Pill, Select, SkeletonSet } from '@cwos/ui-admin';
import type { agents as AgentsApi } from '@cwos/api-client';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { agents } from './agentsClient.js';
import { statusLabel, statusPill, triggerSentence } from './agentsLogic.js';
import {
  AGENTS_CONFIGURE_KEY,
  AGENTS_DEPLOY_KEY,
  agentAuthority,
  agentAuthoritySentence,
  authorityOpen,
  blockedFormSentence,
  consentCopy,
  consentPill,
  decisionConsequence,
  decisionLabel,
  deploymentBlockers,
  enablementCopy,
  enablementPill,
  ledgerEntryLabel,
  ledgerPill,
  ledgerSentence,
  offeredDecisions,
  termsCurrencyNote,
  unofferedReason,
} from './consentLogic.js';

/**
 * INTELLIGENCE → AGENTS → SET UP AN AGENT.
 *
 * ===========================================================================
 * WHY CONSENT AND CONFIGURATION ARE ONE SCREEN
 * ===========================================================================
 * The founder ruled two things on 2026-09-08 and they are one act from the
 * customer's side. Ruling 1: authorized tenant administrators may configure and
 * deploy Phibian-approved agents. The tenant-consent ruling: they may do so
 * only when the organization has itself opted in, revocably, with the
 * acknowledged terms edition on the record.
 *
 * So consent is the gate deployment sits behind. A configuration form that did
 * not show the consent standing would let somebody spend ten minutes building
 * an agent that cannot run, and would then refuse it at the door with a
 * sentence they had no way to anticipate. And a consent control on its own
 * screen would be a governance decision detached from the only thing it
 * decides. One screen, two sections, in the order the acts happen.
 *
 * ===========================================================================
 * WHAT THIS SCREEN DOES NOT DECIDE
 * ===========================================================================
 * IT DOES NOT COMPUTE THE CONJUNCTION. "Agent capability becomes usable only
 * when all are true: platform tenant enablement · active tenant consent ·
 * specific user permission · applicable configuration/deployment authority."
 * `GET /api/agents/catalog` reports the first two as STANDINGS and `/auth/me`
 * reports the reader's grants; `consentLogic.deploymentBlockers` collects the
 * reported refusals into a list and nothing here ANDs them into a verdict. An
 * empty list disables no refusal and grants no permission — it means nothing
 * we were told about is in the way, and the door decides, again, at every fire.
 *
 * IT DOES NOT SHOW PLATFORM AGENTS. `agent-runtime.system-platform-agents` is
 * classed E — internal only, for both personas — and the founder ruled that a
 * platform deployment must bind to a deterministic service principal rather
 * than impersonate a user. The catalog this form reads is the CUSTOMER catalog
 * (`GET /api/agents/catalog`, `agents:configure`), which is generated from the
 * registered definitions and the tool contracts; the platform door lives at
 * `/api/system/agents/**` behind a platform credential, and nothing on this
 * screen reaches it, names it, or offers a control over it.
 *
 * IT DOES NOT PROMISE THE AGENT WILL BE ALLOWED TO ACT. What is delegated at
 * deploy time is not what an agent may do; authority is re-derived at fire time
 * from the live catalog, and a deployer who later loses one key refuses the
 * whole run. The permission list below is labelled as a delegation for exactly
 * that reason, and the verdict lives on the agent's own detail page.
 */

/** The sentence `RequirePermission` shows a reader who lacks the governance key. */
export const AGENT_DEPLOY_IS =
  'Setting up an agent is where your organization decides whether operational agents may run on ' +
  'its data at all, and where somebody chooses what a particular agent is for, what it may touch ' +
  'and what starts it.';

const AUTONOMY_LEVELS: readonly AgentsApi.AgentAutonomyLevel[] = [0, 1, 2, 3, 4];

const AUTONOMY_NOTE: Record<AgentsApi.AgentAutonomyLevel, string> = {
  0: 'Level 0 — propose only. Every action waits for a person.',
  1: 'Level 1 — act, then tell somebody.',
  2: 'Level 2 — act within the configured limits.',
  3: 'Level 3 — act, and ask only for the tools marked as needing approval.',
  4: 'Level 4 — act without asking. Only tools whose own ceiling allows it.',
};

interface FormState {
  definitionId: string;
  name: string;
  objective: string;
  profile: string[];
  triggerKind: 'NONE' | 'MANUAL' | 'SCHEDULE';
  cron: string;
  timezone: string;
  defaultLevel: AgentsApi.AgentAutonomyLevel;
  maxCreditsPerRun: string;
  maxCreditsPerDay: string;
  maxActionsPerRun: string;
  maxRuntimeMinutes: string;
  parameters: Record<string, string>;
}

const EMPTY_FORM: FormState = {
  definitionId: '',
  name: '',
  objective: '',
  profile: [],
  triggerKind: 'NONE',
  cron: '',
  timezone: '',
  defaultLevel: 0,
  maxCreditsPerRun: '',
  maxCreditsPerDay: '',
  maxActionsPerRun: '',
  maxRuntimeMinutes: '',
  parameters: {},
};

const numberOrUndefined = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
};

export function AgentDeploy() {
  const search = useSearch({ strict: false }) as { agent?: string };
  const editingId = search.agent ?? '';
  const navigate = useNav();
  const queryClient = useQueryClient();
  const permissions = usePermissions();

  const governance = agentAuthority(permissions, AGENTS_CONFIGURE_KEY);
  const deployment = agentAuthority(permissions, AGENTS_DEPLOY_KEY);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [prefilled, setPrefilled] = useState('');
  const [decision, setDecision] = useState<AgentsApi.AgentConsentDecision | ''>('');
  const [basis, setBasis] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [consentError, setConsentError] = useState<unknown>(null);
  const [formError, setFormError] = useState<unknown>(null);
  const [draft, setDraft] = useState<AgentsApi.AgentSummary | null>(null);

  const catalog = useQuery({
    queryKey: ['agents', 'catalog'],
    queryFn: () => agents.catalog(),
  });

  /**
   * THE LEDGER IS A SECOND READ ON A SECOND KEY, and that is not an accident
   * of implementation. `GET /api/agents/consent` is `agents:read` — "seeing the
   * decision and making it are different acts" — while the catalog above is
   * `agents:configure`. A reader may hold either without the other, so the
   * history has its own four states and its own refusal, and a 403 here renders
   * as WITHHELD rather than as "this organization has never decided".
   */
  const ledger = useQuery({
    queryKey: ['agents', 'consent'],
    queryFn: () => agents.consent(),
  });

  const existing = useQuery({
    queryKey: ['agents', 'detail', editingId],
    queryFn: () => agents.detail(editingId),
    enabled: editingId !== '',
  });

  const catalogData = catalog.data;
  const entries = useMemo(() => catalogData?.agents ?? [], [catalogData]);
  const chosen = entries.find((entry) => entry.definitionId === form.definitionId) ?? null;

  // PREFILL, ONCE PER AGENT. `existing` refetches; re-seeding the form from it
  // on every render would discard what the reader has typed.
  if (editingId !== '' && existing.data && prefilled !== editingId) {
    const agent = existing.data.agent;
    const trigger = agent.triggers[0];
    setPrefilled(editingId);
    setForm({
      ...EMPTY_FORM,
      definitionId: agent.definitionId,
      name: agent.name,
      objective: agent.objective,
      profile: [...agent.permissionProfile],
      triggerKind: trigger ? (trigger.kind === 'SCHEDULE' ? 'SCHEDULE' : 'MANUAL') : 'NONE',
      cron: trigger?.cron ?? '',
      timezone: trigger?.timezone ?? '',
      defaultLevel: existing.data.approvalPolicy.defaultLevel,
      parameters: Object.fromEntries(
        Object.entries(existing.data.configuration ?? {}).map(([key, value]) => [
          key,
          String(value),
        ]),
      ),
    });
  }

  const blockers = catalogData ? deploymentBlockers(catalogData) : [];
  const blockedSentence = blockedFormSentence(blockers);

  const recordConsent = useMutation({
    mutationFn: (input: {
      decision: AgentsApi.AgentConsentDecision;
      basis: string;
      termsVersion: string;
    }) => agents.recordConsent(input),
    onSuccess: () => {
      setConsentError(null);
      setDecision('');
      setBasis('');
      setAcknowledged(false);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setConsentError(err),
  });

  const create = useMutation({
    mutationFn: (input: AgentsApi.DeployAgentInput) => agents.deploy(input),
    onSuccess: (agent) => {
      setFormError(null);
      setDraft(agent);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setFormError(err),
  });

  const save = useMutation({
    mutationFn: (input: Partial<Omit<AgentsApi.DeployAgentInput, 'definitionId'>>) =>
      agents.configure(editingId, input),
    onSuccess: (agent) => {
      setFormError(null);
      setDraft(agent);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setFormError(err),
  });

  const activate = useMutation({
    mutationFn: (deploymentId: string) => agents.activate(deploymentId),
    onSuccess: (agent) => {
      setFormError(null);
      setDraft(agent);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setFormError(err),
  });

  const triggers = (): AgentsApi.AgentCustomerTrigger[] | undefined => {
    if (form.triggerKind === 'NONE') return undefined;
    if (form.triggerKind === 'MANUAL') return [{ kind: 'MANUAL' }];
    return [
      {
        kind: 'SCHEDULE',
        cron: form.cron.trim(),
        ...(form.timezone.trim() ? { timezone: form.timezone.trim() } : {}),
      },
    ];
  };

  const budget = (): AgentsApi.AgentBudgetChoice | undefined => {
    const runtimeMinutes = numberOrUndefined(form.maxRuntimeMinutes);
    const chosenBudget: AgentsApi.AgentBudgetChoice = {
      ...(numberOrUndefined(form.maxCreditsPerRun) === undefined
        ? {}
        : { maxCreditsPerRun: numberOrUndefined(form.maxCreditsPerRun)! }),
      ...(numberOrUndefined(form.maxCreditsPerDay) === undefined
        ? {}
        : { maxCreditsPerDay: numberOrUndefined(form.maxCreditsPerDay)! }),
      ...(numberOrUndefined(form.maxActionsPerRun) === undefined
        ? {}
        : { maxActionsPerRun: numberOrUndefined(form.maxActionsPerRun)! }),
      ...(runtimeMinutes === undefined
        ? {}
        : { maxRuntimeMs: Math.round(runtimeMinutes * 60_000) }),
    };
    return Object.keys(chosenBudget).length === 0 ? undefined : chosenBudget;
  };

  const parameters = (): Record<string, unknown> | undefined => {
    if (!chosen || chosen.parameters.length === 0) return undefined;
    const out: Record<string, unknown> = {};
    for (const spec of chosen.parameters) {
      const raw = form.parameters[spec.key];
      if (raw === undefined || raw.trim() === '') continue;
      out[spec.key] =
        spec.type === 'number'
          ? Number(raw)
          : spec.type === 'boolean'
            ? raw === 'true'
            : raw.trim();
    }
    return Object.keys(out).length === 0 ? undefined : out;
  };

  const submit = () => {
    const shared = {
      name: form.name.trim(),
      objective: form.objective.trim(),
      permissionProfile: form.profile,
      approvalPolicy: { defaultLevel: form.defaultLevel },
      ...(budget() ? { executionBudget: budget() } : {}),
      ...(triggers() ? { triggers: triggers() } : {}),
    };
    if (editingId !== '') {
      // The form was prefilled from the stored values, and the door REPLACES
      // the bag, so it sends the whole of it (a blank field is the default).
      // Without the catalog entry the fields were never shown: leave it alone.
      save.mutate({ ...shared, ...(chosen ? { configuration: parameters() ?? {} } : {}) });
      return;
    }
    create.mutate({
      definitionId: form.definitionId,
      ...shared,
      ...(parameters() ? { configuration: parameters() } : {}),
    });
  };

  const busy = create.isPending || save.isPending || activate.isPending;
  const canSubmit =
    authorityOpen(editingId === '' ? deployment : governance) &&
    blockers.length === 0 &&
    form.name.trim() !== '' &&
    form.objective.trim() !== '' &&
    form.profile.length > 0 &&
    (editingId !== '' || form.definitionId !== '') &&
    (form.triggerKind !== 'SCHEDULE' || form.cron.trim() !== '');

  const consentStanding = catalogData?.consent ?? ledger.data?.consent ?? null;
  const terms = catalogData?.terms ?? ledger.data?.terms ?? null;
  const offered = consentStanding ? offeredDecisions(consentStanding.state) : [];
  const allDecisions: readonly AgentsApi.AgentConsentDecision[] = [
    'granted',
    'declined',
    'revoked',
  ];
  const consentSentence = agentAuthoritySentence(
    governance,
    'record this organization’s decision about operational agents',
  );
  const deploySentence = agentAuthoritySentence(deployment, 'deploy an agent in this organization');

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <a className="rn-link" href="/agents">
        ← Agents
      </a>

      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>{editingId === '' ? 'Set up an agent' : 'Change this agent'}</h1>
        <p className="rn-muted">
          Two things have to be true before an operational agent runs in your organization, and they
          are independent of each other: Phibian has to have enabled it here, and your organization
          has to have agreed to it. Both are below, with what is missing named rather than implied.
        </p>
      </header>

      {/* ===================================================================
          WHETHER AGENTS MAY RUN HERE
          =================================================================== */}
      <section className="rn-card" data-testid="agent-preconditions">
        <h2 className="rn-card-title">Whether agents may run here</h2>

        {catalog.isLoading ? <SkeletonSet surface="table" rows={3} /> : null}

        {catalog.isError ? (
          <LoadFailure
            error={catalog.error}
            message="We could not read where your organization stands. This is not a statement that agents are switched off."
            refusedHeading="You may not see this organization’s agent standing"
            onRetry={() => void catalog.refetch()}
          />
        ) : null}

        {catalogData ? (
          <div className="rn-col" style={{ gap: 'var(--space-16)' }}>
            <div className="rn-col" style={{ gap: 'var(--space-8)' }} data-standing="enablement">
              <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                <span className="rn-strong">Phibian’s side</span>
                <Pill variant={enablementPill(catalogData.enablement.state)}>
                  {enablementCopy(catalogData.enablement).label}
                </Pill>
              </div>
              <p>{enablementCopy(catalogData.enablement).headline}</p>
              {enablementCopy(catalogData.enablement).remedy ? (
                <p className="rn-muted">{enablementCopy(catalogData.enablement).remedy}</p>
              ) : null}
            </div>

            <div className="rn-col" style={{ gap: 'var(--space-8)' }} data-standing="consent">
              <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                <span className="rn-strong">Your organization’s side</span>
                <Pill variant={consentPill(catalogData.consent.state)}>
                  {consentCopy(catalogData.consent).label}
                </Pill>
              </div>
              <p>{consentCopy(catalogData.consent).headline}</p>
              {consentCopy(catalogData.consent).remedy ? (
                <p className="rn-muted">{consentCopy(catalogData.consent).remedy}</p>
              ) : null}
              {termsCurrencyNote(catalogData.consent) ? (
                <p className="rn-muted">{termsCurrencyNote(catalogData.consent)}</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      {/* ===================================================================
          THE DECISION
          =================================================================== */}
      <section className="rn-card" data-testid="agent-consent-decision">
        <h2 className="rn-card-title">Record a decision</h2>

        {terms ? (
          <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
            <p className="rn-strong">Operational agent terms, edition {terms.version}</p>
            <p>{terms.summary}</p>
          </div>
        ) : null}

        {consentSentence ? (
          <p className="rn-muted" data-consent-gate={governance.kind}>
            {consentSentence}
          </p>
        ) : null}

        {authorityOpen(governance) && consentStanding && terms ? (
          <div className="rn-col" style={{ gap: 'var(--space-12)' }}>
            {consentError ? (
              <LoadFailure
                error={consentError}
                message="That decision was not recorded. Nothing has changed."
                refusedHeading="You may not record this decision"
              />
            ) : null}

            <Select
              label="Decision"
              value={decision}
              onChange={(e) => setDecision(e.target.value as AgentsApi.AgentConsentDecision | '')}
            >
              <option value="">Choose…</option>
              {allDecisions.map((option) => {
                const why = unofferedReason(consentStanding.state, option);
                return (
                  <option key={option} value={option} disabled={why !== null}>
                    {decisionLabel(option)}
                  </option>
                );
              })}
            </Select>

            {/* WHY THE OTHER TWO ARE NOT ON OFFER, in the words of the database
                rule that refuses them. A disabled option with no explanation is
                a dead end; each of these is a fact about the ledger. */}
            <ul className="rn-list">
              {allDecisions.map((option) => {
                const why = unofferedReason(consentStanding.state, option);
                return why === null ? null : (
                  <li key={option} className="rn-muted">
                    <strong>{decisionLabel(option)}:</strong> {why}
                  </li>
                );
              })}
            </ul>

            {decision !== '' ? <p>{decisionConsequence(decision)}</p> : null}

            <Input
              label="Why"
              hint="Recorded with the decision and never editable afterwards. An acceptance whose basis nobody wrote down is one nobody can review."
              value={basis}
              maxLength={2000}
              onChange={(e) => setBasis(e.target.value)}
            />

            {/* THE ACKNOWLEDGEMENT IS AN ECHO, NOT A STAMP. The route refuses
                any version but the published one, and a client that sent the
                current edition by default would be recording that the
                organization agreed to whatever we happened to be shipping. */}
            <label className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>
                I have read edition {terms.version} of the operational agent terms and am recording
                this decision against that edition.
              </span>
            </label>

            <div>
              <Button
                variant="primary"
                disabled={
                  decision === '' ||
                  !offered.includes(decision) ||
                  basis.trim() === '' ||
                  !acknowledged ||
                  recordConsent.isPending
                }
                onClick={() =>
                  decision === ''
                    ? undefined
                    : recordConsent.mutate({
                        decision,
                        basis: basis.trim(),
                        termsVersion: terms.version,
                      })
                }
              >
                {decision === '' ? 'Record this decision' : decisionLabel(decision)}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      {/* ===================================================================
          THE HISTORY
          =================================================================== */}
      <section className="rn-card" data-testid="agent-consent-history">
        <h2 className="rn-card-title">Every decision this organization has recorded</h2>
        <p className="rn-muted">
          The ledger is append-only. Withdrawing consent is a new entry, not an edit, so an
          organization that consented and later withdrew can still see the window in which it had
          consented.
        </p>

        {ledger.isLoading ? <SkeletonSet surface="table" rows={3} /> : null}

        {ledger.isError ? (
          <LoadFailure
            error={ledger.error}
            message="We could not read the decision history. This is not a statement that no decision has been made."
            refusedHeading="The decision history is withheld from you"
            onRetry={() => void ledger.refetch()}
          />
        ) : null}

        {ledger.data ? (
          <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
            <p className="rn-muted">{ledgerSentence(ledger.data.history)}</p>
            <ul className="rn-list">
              {ledger.data.history.entries.map((entry) => (
                <li key={entry.version} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                    <Pill variant={ledgerPill(entry.decision)}>
                      {ledgerEntryLabel(entry.decision)}
                    </Pill>
                    <time dateTime={new Date(entry.decidedAt).toISOString()}>
                      {new Date(entry.decidedAt).toLocaleString()}
                    </time>
                  </div>
                  <span className="rn-muted">
                    Decision {entry.version} · terms edition {entry.termsVersion} · recorded by{' '}
                    <code>{entry.decidedByUserId}</code>
                  </span>
                  <span>{entry.decisionBasis}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {/* ===================================================================
          THE AGENT
          =================================================================== */}
      <section className="rn-card" data-testid="agent-configuration">
        <h2 className="rn-card-title">
          {editingId === '' ? 'The agent you are setting up' : 'What this agent does'}
        </h2>

        {blockedSentence ? (
          <div className="rn-col" style={{ gap: 'var(--space-8)' }} data-testid="agent-blocked">
            <p className="rn-strong">{blockedSentence}</p>
            <ul className="rn-list">
              {blockers.map((blocker) => (
                <li key={blocker.id} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <span>{blocker.headline}</span>
                  {blocker.remedy ? <span className="rn-muted">{blocker.remedy}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {editingId === '' && deploySentence ? (
          <p className="rn-muted" data-deploy-gate={deployment.kind}>
            {deploySentence}
          </p>
        ) : null}

        {editingId !== '' && consentSentence ? <p className="rn-muted">{consentSentence}</p> : null}

        {editingId !== '' && existing.isError ? (
          <LoadFailure
            error={existing.error}
            message="We could not read this agent. Nothing has been changed."
            refusedHeading="You may not read this agent"
            onRetry={() => void existing.refetch()}
          />
        ) : null}

        {editingId !== '' && existing.data ? (
          <p className="rn-muted">
            <Pill variant={statusPill(existing.data.agent.status)}>
              {statusLabel(existing.data.agent.status)}
            </Pill>{' '}
            {existing.data.agent.status === 'ACTIVE'
              ? 'An active agent cannot be reconfigured. Pause it first, so a run in flight is never operating under a profile that has already been replaced.'
              : 'Configuration changes take effect the next time this agent runs.'}
          </p>
        ) : null}

        {formError ? (
          <LoadFailure
            error={formError}
            message="That did not go through. Nothing has been created or changed."
            refusedHeading="You may not do that"
          />
        ) : null}

        {catalog.isLoading || (editingId !== '' && existing.isLoading) ? (
          <SkeletonSet surface="drawer" rows={5} />
        ) : null}

        {catalogData ? (
          <div className="rn-col" style={{ gap: 'var(--space-12)' }}>
            {editingId === '' ? (
              <Select
                label="Which agent"
                hint="This is the whole set your organization may deploy. It is generated from the agents Phibian has built and cannot be extended by a request."
                value={form.definitionId}
                onChange={(e) =>
                  setForm({ ...form, definitionId: e.target.value, profile: [], parameters: {} })
                }
              >
                <option value="">Choose…</option>
                {entries.map((entry) => (
                  <option key={entry.definitionId} value={entry.definitionId}>
                    {entry.name}
                  </option>
                ))}
              </Select>
            ) : null}

            {chosen ? <p className="rn-muted">{chosen.description}</p> : null}

            <Input
              label="Name"
              value={form.name}
              maxLength={120}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label="Describe the job in plain English"
              hint="For example: watch open documentation every day and remind people when something is overdue. Stored as this agent’s objective and shown wherever it appears; what it may actually do is set by the choices below."
              value={form.objective}
              maxLength={2000}
              onChange={(e) => setForm({ ...form, objective: e.target.value })}
            />

            {chosen ? (
              <fieldset className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <legend className="rn-strong">Data access — what it is allowed to be given</legend>
                {/* THE PROFILE IS A DELEGATION, NOT A CAPABILITY. It is what
                    this agent is handed at deploy time; what it may actually do
                    is re-derived at fire time from the deployer's live holds,
                    four times per run. The wording here has to survive a
                    deployer losing a key tomorrow. */}
                <p className="rn-muted">
                  These are the permissions this agent’s tools ask for. Choosing one delegates it —
                  it does not grant it. At every run the agent’s authority is worked out again from
                  what the person who deployed it holds at that moment, so a key you no longer hold
                  refuses the run whatever is ticked here.
                </p>
                {chosen.permissionCeiling.map((key) => (
                  <label key={key} className="rn-row" style={{ gap: 'var(--space-8)' }}>
                    <input
                      type="checkbox"
                      checked={form.profile.includes(key)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          profile: e.target.checked
                            ? [...form.profile, key]
                            : form.profile.filter((held) => held !== key),
                        })
                      }
                    />
                    <code>{key}</code>
                  </label>
                ))}
                {chosen.permissionCeiling.length === 0 ? (
                  <p className="rn-muted">
                    This agent’s tools ask for no permissions at all. That is a measured empty set,
                    not a missing one.
                  </p>
                ) : null}
              </fieldset>
            ) : null}

            {chosen ? (
              <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <Select
                  label="What starts it"
                  hint="A schedule and a manual start are the only two an organization may configure. The other trigger kinds exist and are not offered here."
                  value={form.triggerKind}
                  onChange={(e) =>
                    setForm({ ...form, triggerKind: e.target.value as FormState['triggerKind'] })
                  }
                >
                  <option value="NONE">Nothing — leave it unstarted</option>
                  {chosen.triggerKinds.includes('MANUAL') ? (
                    <option value="MANUAL">Somebody starts it by hand</option>
                  ) : null}
                  {chosen.triggerKinds.includes('SCHEDULE') ? (
                    <option value="SCHEDULE">On a schedule</option>
                  ) : null}
                </Select>
                {form.triggerKind === 'SCHEDULE' ? (
                  <>
                    <Input
                      label="Schedule"
                      hint="A cron expression, for example 0 9 * * 1-5 for weekday mornings."
                      value={form.cron}
                      onChange={(e) => setForm({ ...form, cron: e.target.value })}
                    />
                    <Input
                      label="Time zone"
                      hint="An IANA zone such as America/Chicago. Left blank, the schedule runs in UTC — a schedule read in the wrong zone fires at the wrong hour."
                      value={form.timezone}
                      onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                    />
                  </>
                ) : null}
                {form.triggerKind === 'NONE' ? (
                  <p className="rn-muted">
                    Nothing will start this agent. That is a real choice and not an oversight — it
                    will sit configured and never run until a trigger is added.
                  </p>
                ) : null}
              </div>
            ) : null}

            {chosen ? (
              <Select
                label="How much it may do without asking"
                hint={AUTONOMY_NOTE[form.defaultLevel]}
                value={String(form.defaultLevel)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    defaultLevel: Number(e.target.value) as AgentsApi.AgentAutonomyLevel,
                  })
                }
              >
                {AUTONOMY_LEVELS.map((level) => (
                  <option key={level} value={String(level)}>
                    {AUTONOMY_NOTE[level]}
                  </option>
                ))}
              </Select>
            ) : null}

            {chosen ? (
              <fieldset className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <legend className="rn-strong">Limits</legend>
                <p className="rn-muted">
                  Leave a field blank to use the default. Each one has a ceiling your organization
                  cannot raise from here; the ceiling is shown beside the field.
                </p>
                <Input
                  label="Credits per run"
                  type="number"
                  min={1}
                  max={chosen.budgetCeilings.maxCreditsPerRun}
                  hint={`At most ${chosen.budgetCeilings.maxCreditsPerRun}`}
                  value={form.maxCreditsPerRun}
                  onChange={(e) => setForm({ ...form, maxCreditsPerRun: e.target.value })}
                />
                <Input
                  label="Credits per day"
                  type="number"
                  min={1}
                  max={chosen.budgetCeilings.maxCreditsPerDay}
                  hint={`At most ${chosen.budgetCeilings.maxCreditsPerDay}`}
                  value={form.maxCreditsPerDay}
                  onChange={(e) => setForm({ ...form, maxCreditsPerDay: e.target.value })}
                />
                <Input
                  label="Actions per run"
                  type="number"
                  min={1}
                  max={chosen.budgetCeilings.maxActionsPerRun}
                  hint={`At most ${chosen.budgetCeilings.maxActionsPerRun}`}
                  value={form.maxActionsPerRun}
                  onChange={(e) => setForm({ ...form, maxActionsPerRun: e.target.value })}
                />
                <Input
                  label="Minutes per run"
                  type="number"
                  min={1}
                  max={Math.round(chosen.budgetCeilings.maxRuntimeMs / 60_000)}
                  hint={`At most ${Math.round(chosen.budgetCeilings.maxRuntimeMs / 60_000)}`}
                  value={form.maxRuntimeMinutes}
                  onChange={(e) => setForm({ ...form, maxRuntimeMinutes: e.target.value })}
                />
              </fieldset>
            ) : null}

            {chosen && chosen.parameters.length > 0 ? (
              <fieldset
                className="rn-col"
                style={{ gap: 'var(--space-8)' }}
                data-testid="agent-parameters"
              >
                <legend className="rn-strong">Settings for this agent</legend>
                {chosen.parameters.map((spec) =>
                  spec.type === 'boolean' ? (
                    <label key={spec.key} className="rn-row" style={{ gap: 'var(--space-8)' }}>
                      <input
                        type="checkbox"
                        checked={
                          (form.parameters[spec.key] ?? String(spec.defaultValue)) === 'true'
                        }
                        onChange={(e) =>
                          setForm({
                            ...form,
                            parameters: {
                              ...form.parameters,
                              [spec.key]: String(e.target.checked),
                            },
                          })
                        }
                      />
                      {spec.label}
                    </label>
                  ) : (
                    <Input
                      key={spec.key}
                      label={spec.label}
                      hint={`Leave blank for the default (${String(spec.defaultValue)}).`}
                      value={form.parameters[spec.key] ?? ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          parameters: { ...form.parameters, [spec.key]: e.target.value },
                        })
                      }
                    />
                  ),
                )}
              </fieldset>
            ) : null}

            <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
              <Button variant="primary" disabled={!canSubmit || busy} onClick={submit}>
                {editingId === '' ? 'Create it, switched off' : 'Save these changes'}
              </Button>
              {editingId === '' ? (
                <span className="rn-muted">
                  It is created switched off. Turning it on is a second act with its own record.
                </span>
              ) : null}
            </div>

            {draft ? (
              <div className="rn-col" style={{ gap: 'var(--space-8)' }} data-testid="agent-draft">
                <p className="rn-strong">
                  {draft.name} —{' '}
                  <Pill variant={statusPill(draft.status)}>{statusLabel(draft.status)}</Pill>
                </p>
                <p className="rn-muted">
                  {draft.triggers.length === 0
                    ? 'Nothing starts it.'
                    : draft.triggers.map((t) => triggerSentence(t)).join(' · ')}
                </p>
                <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
                  {authorityOpen(deployment) && draft.status !== 'ACTIVE' ? (
                    <Button
                      variant="primary"
                      disabled={activate.isPending}
                      onClick={() => activate.mutate(draft.id)}
                    >
                      Turn it on
                    </Button>
                  ) : null}
                  <Button variant="secondary" onClick={() => navigate(`/agents/${draft.id}`)}>
                    Open it
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </article>
  );
}
